// The chat live-preview SSE TRANSPORT (split from api-client.ts under the file-size ceiling): the
// frame parser + the fetch/ReadableStream reader. The transport dispatches only — it fabricates no
// turn (P-SELF); every value comes off the guard-cleared frame, and the snapshot read remains the
// reconciliation authority (streaming §F).

import {
  isChatActivityKind,
  isChatActivityOutcome,
  isChatReasoningText,
  type ChatActivityItem,
  type ChatActivityRow,
  type ChatPlanView,
  type ChatRunState,
} from './projections.ts';
import type { ChatStreamHandlers } from './api-client.ts';

/** Mirrors the server's deliberately narrow opaque-id/catalog-name alphabets. */
const ACTIVITY_CALL_ID = /^[A-Za-z0-9_-]{1,96}$/;
const CATALOG_TOOL_NAME = /^ritona_[a-z0-9_]{1,88}$/;
const PLAN_STEP_ID = /^pstep_[A-Za-z0-9_-]{1,64}$/;

/** Reject C0/DEL characters without embedding control ranges in a regular expression. */
function containsControlText(value: string): boolean {
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f || codePoint === 0x7f;
  });
}

/**
 * Parse ONE SSE frame (`event: <kind>\ndata: <json>`) and dispatch it to the matching handler. Unknown
 * events are ignored (forward-compatible); a malformed `data:` is dropped (never surfaced verbatim — G-197).
 * The transport dispatches only — it fabricates no turn (P-SELF); every value comes off the guard-cleared frame.
 * Returns TRUE only when it dispatched a terminal `end` frame (the caller uses this to detect a bare EOF that
 * carried NO `end` — heartbeat teardown, the close after a `refused`, a network drop — and reconcile then).
 * Exported so the parser is unit-testable against raw frame strings (§engine-review #9).
 */
export function dispatchFrame(frame: string, handlers: ChatStreamHandlers): boolean {
  let event = '';
  const dataLines: string[] = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) event = line.slice('event:'.length).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice('data:'.length).replace(/^ /, ''));
  }
  if (dataLines.length === 0) return false;
  let payload: Record<string, unknown>;
  try {
    payload = JSON.parse(dataLines.join('\n')) as Record<string, unknown>;
  } catch {
    return false; // a malformed frame is dropped, never rendered (the snapshot read remains the authority)
  }
  const text = payload['text'];
  const sequence = payload['sequence'];
  const reason = payload['reason'];
  const state = payload['state'];
  if (event === 'delta' && typeof text === 'string') {
    handlers.onDelta(typeof sequence === 'number' ? sequence : 0, text);
  } else if (event === 'snapshot' && typeof text === 'string') {
    handlers.onSnapshot(text);
  } else if (event === 'reasoning' && isChatReasoningText(text)) {
    // The client independently enforces the fixed platform vocabulary. An arbitrary server/model string is
    // dropped here and can never become an Assistant UI reasoning part.
    handlers.onReasoning?.(text);
  } else if (event === 'reasoning_summary' && isProviderReasoningSummary(text)) {
    handlers.onReasoningSummary?.(text);
  } else if (event === 'plan') {
    const plan = asChatPlan(payload['plan']);
    if (plan !== undefined) handlers.onPlan?.(plan);
  } else if (event === 'refused' && typeof reason === 'string') {
    handlers.onRefused(reason);
  } else if (event === 'end' && typeof state === 'string') {
    handlers.onEnd(state as ChatRunState);
    return true;
  } else if (event === 'activity_start' || event === 'activity') {
    dispatchActivityFrame(event, payload, handlers);
  }
  return false;
}

/** Independent browser guard for the persisted Agent plan projection. */
function asChatPlan(value: unknown): ChatPlanView | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
  const row = value as Record<string, unknown>;
  const revision = row['revision'];
  const updatedAt = row['updatedAt'];
  const rawSteps = row['steps'];
  if (
    typeof revision !== 'number' ||
    !Number.isSafeInteger(revision) ||
    revision < 1 ||
    !isCanonicalIsoTimestamp(updatedAt) ||
    !Array.isArray(rawSteps) ||
    rawSteps.length > 40
  ) {
    return undefined;
  }
  const steps = rawSteps.map(asPlanStep);
  if (steps.some((step) => step === undefined)) return undefined;
  return { revision, updatedAt, steps: steps as ChatPlanView['steps'] };
}

function asPlanStep(value: unknown): ChatPlanView['steps'][number] | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
  const row = value as Record<string, unknown>;
  const id = row['id'];
  const text = row['text'];
  const status = row['status'];
  if (
    typeof id !== 'string' ||
    !PLAN_STEP_ID.test(id) ||
    typeof text !== 'string' ||
    text.length === 0 ||
    Array.from(text).length > 400 ||
    text !== text.trim() ||
    containsControlText(text) ||
    (status !== 'pending' && status !== 'in_progress' && status !== 'done' && status !== 'blocked')
  ) {
    return undefined;
  }
  return { id, text, status };
}

function isCanonicalIsoTimestamp(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > 32) return false;
  const timestamp = new Date(value);
  return !Number.isNaN(timestamp.getTime()) && timestamp.toISOString() === value;
}

/** Independent browser boundary for the server's provider-summary frame. The text has already crossed the
 * server egress guard, but the browser still fails closed on multiline, control-bearing or oversized copy. */
function isProviderReasoningSummary(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.length <= 160 &&
    value === value.trim() &&
    !containsControlText(value)
  );
}

/** Dispatch only the two allowlisted activity wire shapes; drifted correlation degrades per field. */
function dispatchActivityFrame(
  event: 'activity_start' | 'activity',
  payload: Readonly<Record<string, unknown>>,
  handlers: ChatStreamHandlers,
): void {
  if (event === 'activity_start') {
    const started = asActivityStart(payload['activity']);
    if (started !== undefined) handlers.onActivity?.(started);
    return;
  }
  const settled = asActivityRow(payload['activity']);
  if (settled === undefined) return;
  handlers.onActivity?.({ ...settled, ...asActivityCorrelation(payload) });
}

/** DEGRADE, never reject: the server filters callId and toolName INDEPENDENTLY (spawn-output-bridge),
 *  so a half-valid pair is legal on the wire. Dropping the whole terminal for one drifted field left its
 *  running card at "working…" forever; keeping each field that validates still settles the card, and the
 *  per-field alphabets stay enforced (an invalid value never reaches the view). */
function asActivityCorrelation(payload: Readonly<Record<string, unknown>>): {
  readonly toolCallId?: string;
  readonly toolName?: string;
} {
  const callId = payload['callId'];
  const toolName = payload['toolName'];
  return {
    ...(typeof callId === 'string' && ACTIVITY_CALL_ID.test(callId) ? { toolCallId: callId } : {}),
    ...(typeof toolName === 'string' && CATALOG_TOOL_NAME.test(toolName) ? { toolName } : {}),
  };
}

/** Narrow an unknown wire value to a ChatActivityRow, or undefined (dropped — never rendered raw). */
function asActivityRow(value: unknown): ChatActivityRow | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const row = value as Record<string, unknown>;
  const id = row['id'];
  const kind = row['kind'];
  const label = row['label'];
  const sequence = row['sequence'];
  const outcome = row['outcome'];
  if (
    typeof id !== 'string' ||
    id.length === 0 ||
    !isChatActivityKind(kind) ||
    typeof label !== 'string' ||
    label.length === 0 ||
    typeof sequence !== 'number' ||
    !Number.isInteger(sequence) ||
    sequence < 0 ||
    !isChatActivityOutcome(outcome)
  ) {
    return undefined;
  }
  const objectRef = row['objectRef'];
  return {
    id,
    kind,
    label,
    outcome,
    sequence,
    ...(typeof objectRef === 'object' && objectRef !== null && !Array.isArray(objectRef)
      ? { objectRef: objectRef as Readonly<Record<string, unknown>> }
      : {}),
  };
}

/** Narrow the deliberately tiny ephemeral tool-start wire shape to a running activity item. The card's
 *  identity (callId), vocabulary (kind), copy (label) and status stay required; a drifted toolName only
 *  costs THAT field, never the whole running card (degrade-not-reject — correlation still rides callId). */
function asActivityStart(value: unknown): ChatActivityItem | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const row = value as Record<string, unknown>;
  const callId = row['callId'];
  const kind = row['kind'];
  const label = row['label'];
  const toolName = row['toolName'];
  if (
    typeof callId !== 'string' ||
    !ACTIVITY_CALL_ID.test(callId) ||
    !isChatActivityKind(kind) ||
    typeof label !== 'string' ||
    label.length === 0 ||
    row['status'] !== 'running'
  ) {
    return undefined;
  }
  return {
    id: callId,
    toolCallId: callId,
    kind,
    label,
    ...(typeof toolName === 'string' && CATALOG_TOOL_NAME.test(toolName) ? { toolName } : {}),
    outcome: 'running',
  };
}

/**
 * Open the live-preview SSE stream via `fetch` + a `ReadableStream` reader (NOT `EventSource` — so it sends
 * `credentials:'same-origin'` and is abortable). It parses `event:`/`data:` frames split on the blank-line
 * boundary and dispatches each to the handlers. The returned unsubscribe aborts the fetch (closing the
 * server subscription). A non-OK response / socket drop calls `onError` so the caller falls back to the
 * authoritative snapshot read — the stream is never the reconciliation authority (streaming §F).
 */
export function streamRun(path: string, handlers: ChatStreamHandlers): () => void {
  const controller = new AbortController();
  void (async (): Promise<void> => {
    try {
      const res = await fetch(path, {
        method: 'GET',
        headers: { accept: 'text/event-stream' },
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (!res.ok || res.body === null) {
        handlers.onError();
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let sawEnd = false;
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx: number;
        while ((idx = buf.indexOf('\n\n')) !== -1) {
          const frame = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          if (frame.trim().length > 0 && dispatchFrame(frame, handlers)) sawEnd = true;
        }
      }
      // A BARE EOF that carried no `end` frame (a heartbeat teardown, the close after a `refused`, a network
      // drop) must STILL reconcile — otherwise the client stays wedged in 'running' with the preview surfaced
      // (§engine-review #2/#3). `onError` falls the caller back to the authoritative snapshot read (snapshot wins).
      if (!sawEnd && !controller.signal.aborted) handlers.onError();
    } catch {
      if (!controller.signal.aborted) handlers.onError();
    }
  })();
  return () => controller.abort();
}
